import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import "./App.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { staggerChildren: 0.2 },
};

const staggerChild = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const BOTTLE_IMG = "https://customer-assets.emergentagent.com/job_711591c3-dcda-4256-aa84-0f6ff0194d9f/artifacts/bxohtnbw_Bottle%202.jpg";

const FaqItem = ({ question, answer, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="border-b border-white/5"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left group"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-body text-base text-slate-200 font-light pr-8 group-hover:text-white transition-colors">{question}</span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-slate-500 flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="text-sm text-slate-400 font-light leading-relaxed pb-6">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const App = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [formState, setFormState] = useState("idle"); // idle | sending | success | error
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState("sending");
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Network error");
      setFormState("success");
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setFormState("idle"), 5000);
    } catch {
      setFormState("error");
      setTimeout(() => setFormState("idle"), 4000);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] font-body">
      {/* ─── Navigation ─── */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-slate-950/80 backdrop-blur-2xl border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center h-20">
            <a href="#" className="font-heading text-2xl tracking-widest text-white font-light" data-testid="nav-logo">
              KE ALOHA
            </a>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-10">
              <a href="#story" className="text-sm font-body font-light tracking-wide text-slate-300 hover:text-white transition-colors duration-300" data-testid="nav-story-link">
                Our Story
              </a>
              <a href="#products" className="text-sm font-body font-light tracking-wide text-slate-300 hover:text-white transition-colors duration-300" data-testid="nav-products-link">
                Products
              </a>
              <a href="#faq" className="text-sm font-body font-light tracking-wide text-slate-300 hover:text-white transition-colors duration-300" data-testid="nav-faq-link">
                FAQ
              </a>
              <a
                href="#contact"
                className="text-sm font-body font-medium tracking-wide text-slate-950 bg-white px-6 py-2.5 hover:bg-slate-200 transition-colors duration-300"
                data-testid="nav-contact-link"
              >
                Get in Touch
              </a>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenu(!mobileMenu)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8h16M4 16h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-slate-950/95 backdrop-blur-2xl border-t border-white/10 overflow-hidden"
            >
              <div className="px-6 py-6 flex flex-col gap-5">
                <a href="#story" onClick={() => setMobileMenu(false)} className="text-slate-300 hover:text-white transition-colors">Our Story</a>
                <a href="#products" onClick={() => setMobileMenu(false)} className="text-slate-300 hover:text-white transition-colors">Products</a>
                <a href="#faq" onClick={() => setMobileMenu(false)} className="text-slate-300 hover:text-white transition-colors">FAQ</a>
                <a href="#contact" onClick={() => setMobileMenu(false)} className="text-white bg-white/10 px-4 py-2 text-center border border-white/20">Get in Touch</a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* ─── Hero Section ─── */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/27593123/pexels-photo-27593123.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950/70 to-[#020617] z-10" />

        <motion.div style={{ opacity: heroOpacity }} className="relative z-20 text-center px-6 max-w-5xl mx-auto pt-20">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xs font-body font-semibold tracking-[0.25em] text-slate-300 uppercase mb-8"
          >
            Sacred Hydration &middot; Wellness in a Bottle
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-8xl font-light text-white leading-[0.95] mb-6"
            data-testid="hero-title"
          >
            KE ALOHA
            <span className="block text-sky-300 mt-2 text-3xl sm:text-4xl lg:text-5xl tracking-[0.15em] font-light">
              WATER
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="text-base sm:text-lg text-slate-400 font-light max-w-2xl mx-auto mb-12 leading-relaxed"
          >
            In Hawaiian, KE ALOHA means "The Love"—and that's exactly what we pour into every bottle.
          </motion.p>

          {/* Bottle hero */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="flex justify-center mb-12 relative"
          >
            {/* Radial glow behind bottle */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-80 h-80 sm:w-[400px] sm:h-[400px] md:w-[500px] md:h-[500px] bg-sky-400/8 rounded-full blur-[80px]" />
            </div>
            <motion.img
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              src={BOTTLE_IMG}
              alt="KE ALOHA Water Bottle"
              className="h-72 sm:h-96 md:h-[480px] object-contain relative z-10"
              style={{
                filter: "drop-shadow(0 20px 60px rgba(56,189,248,0.12))",
                WebkitMaskImage: "radial-gradient(ellipse 48% 70% at 50% 50%, black 30%, transparent 75%)",
                maskImage: "radial-gradient(ellipse 48% 70% at 50% 50%, black 30%, transparent 75%)",
              }}
              data-testid="hero-bottle-image"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="#products"
              className="px-8 py-4 bg-white text-slate-950 font-medium text-sm tracking-wide hover:bg-slate-200 transition-colors duration-300"
              data-testid="hero-explore-button"
            >
              Explore Products
            </a>
            <a
              href="#contact"
              className="px-8 py-4 bg-transparent border border-white/20 text-white font-medium text-sm tracking-wide hover:bg-white/5 transition-colors duration-300"
              data-testid="hero-contact-button"
            >
              Get in Touch
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent"
          />
        </motion.div>
      </section>

      {/* ─── Brand Story Section ─── */}
      <section id="story" className="py-24 md:py-32 bg-[#020617]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 md:gap-24 items-center">
            <motion.div {...fadeUp}>
              <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">Our Story</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white leading-tight mb-8" data-testid="story-heading">
                Sacred<br />Hydration
              </h2>
              <p className="text-base sm:text-lg text-slate-400 font-light leading-relaxed mb-6">
                Sourced fresh, our impeccably clean water offers an unmistakably crisp and refreshing taste.
                Every bottle is a moment of clarity—untouched, consistent, and deeply revitalizing.
              </p>
              <p className="text-base sm:text-lg text-slate-400 font-light leading-relaxed mb-10">
                This is more than hydration—it's a connection to nature. Sacred Hydration. Wellness in a bottle.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-px h-10 bg-sky-400/40" />
                <span className="text-sm text-slate-300 font-light tracking-wide">100% Natural Source</span>
              </div>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/30749406/pexels-photo-30749406.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=600"
                  alt="Hawaiian Ocean"
                  className="w-full h-[500px] object-cover"
                  data-testid="story-image"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Products Section ─── */}
      <section id="products" className="py-24 md:py-32 bg-[#0a0f1e]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-20">
            <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">Our Collection</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white" data-testid="products-heading">
              Pure Perfection
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto">
            {/* 500ml */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-slate-900/40 backdrop-blur-xl border border-white/10 p-10 hover:bg-slate-900/60 hover:border-white/20 transition-all duration-500"
              data-testid="product-500ml"
            >
              <div className="flex justify-center mb-10">
                <motion.img
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  src={BOTTLE_IMG}
                  alt="500ml KE ALOHA Water"
                  className="h-56 object-contain group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="text-center">
                <h3 className="font-heading text-3xl font-light text-white mb-3">500ml</h3>
                <p className="text-slate-400 font-light text-sm leading-relaxed">
                  Perfect for on-the-go hydration. Portable, pure, and always refreshing.
                </p>
              </div>
            </motion.div>

            {/* 1 Liter */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-slate-900/40 backdrop-blur-xl border border-white/10 p-10 hover:bg-slate-900/60 hover:border-white/20 transition-all duration-500"
              data-testid="product-1liter"
            >
              <div className="flex justify-center mb-10">
                <motion.img
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  src={BOTTLE_IMG}
                  alt="1 Liter KE ALOHA Water"
                  className="h-56 object-contain scale-110 group-hover:scale-[1.15] transition-transform duration-700"
                />
              </div>
              <div className="text-center">
                <h3 className="font-heading text-3xl font-light text-white mb-3">1 Liter</h3>
                <p className="text-slate-400 font-light text-sm leading-relaxed">
                  Ideal for home, office, or extended adventures. Extended wellness throughout your day.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Benefits Section ─── */}
      <section className="py-24 md:py-32 bg-[#020617] relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sky-500/5 rounded-full blur-[120px]" />

        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="text-center mb-20">
            <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">The Difference</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white" data-testid="benefits-heading">
              Wellness in Every Drop
            </h2>
          </motion.div>

          <motion.div {...staggerContainer} className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                title: "Crisp & Refreshing",
                desc: "An unmistakably clean taste that revitalizes your senses with every sip.",
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                  </svg>
                ),
              },
              {
                title: "Untouched Purity",
                desc: "Every bottle maintains consistent quality and clarity from source to sip.",
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
              {
                title: "Nature Connection",
                desc: "More than hydration—a moment of clarity and deep revitalization.",
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                ),
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                {...staggerChild}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`benefit-${i}`}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 border border-white/10 text-sky-300 mb-6">
                  {item.icon}
                </div>
                <h3 className="font-heading text-xl font-light text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 font-light text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Where to Find Us ─── */}
      <section className="py-24 md:py-32 bg-[#0a0f1e]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">Availability</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white" data-testid="locations-heading">
              Where to Find Us
            </h2>
          </motion.div>

          <motion.div {...staggerContainer} className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Honolulu Airport", sub: "Daniel K. Inouye International (HNL)", island: "Oahu" },
              { name: "Kahului Airport", sub: "Maui Airport (OGG)", island: "Maui" },
              { name: "Lihue Airport", sub: "Kauai Airport (LIH)", island: "Kauai" },
            ].map((loc, i) => (
              <motion.div
                key={i}
                {...staggerChild}
                viewport={{ once: true }}
                className="bg-slate-900/40 backdrop-blur-xl border border-white/10 p-8 text-center hover:border-white/20 transition-all duration-500"
                data-testid={`location-${i}`}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 border border-white/10 text-sky-300 mb-5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                  </svg>
                </div>
                <h3 className="font-heading text-lg font-light text-white mb-1">{loc.name}</h3>
                <p className="text-slate-500 text-xs font-light mb-2">{loc.sub}</p>
                <p className="text-sky-400/70 text-xs font-semibold tracking-widest uppercase">{loc.island}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.p {...fadeUp} className="text-center text-sm text-slate-500 font-light mt-10">
            For bulk orders, events, and distribution inquiries, contact <a href="mailto:everything@8eight8.net" className="text-slate-300 hover:text-white transition-colors">everything@8eight8.net</a>
          </motion.p>
        </div>
      </section>

      {/* ─── FAQ Section ─── */}
      <section id="faq" className="py-24 md:py-32 bg-[#020617]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">Questions</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white" data-testid="faq-heading">
              Frequently Asked
            </h2>
          </motion.div>

          <motion.div {...staggerContainer} className="space-y-0">
            {[
              {
                q: "What is Ke Aloha Water?",
                a: "Ke Aloha Water is a premium bottled water. In Hawaiian, Ke Aloha means \"The Love.\" The brand is built around the concept of sacred hydration and wellness, offering impeccably clean water with a crisp and refreshing taste.",
              },
              {
                q: "What does \"Ke Aloha\" mean?",
                a: "In Hawaiian, Ke Aloha means \"The Love.\" That spirit of love and care is poured into every bottle.",
              },
              {
                q: "What sizes does Ke Aloha Water come in?",
                a: "Ke Aloha Water is available in two sizes: 500ml bottles for portable, on-the-go hydration and 1 Liter bottles ideal for home, office, or extended use.",
              },
              {
                q: "Where can I buy Ke Aloha Water?",
                a: "Ke Aloha Water is currently available at Honolulu International Airport (Daniel K. Inouye), Kahului Airport on Maui, and Lihue Airport on Kauai.",
              },
              {
                q: "Can I order Ke Aloha Water in bulk?",
                a: "Yes. Ke Aloha Water is available for bulk orders. Contact everything@8eight8.net with your requirements and a team member will follow up.",
              },
              {
                q: "Is Ke Aloha Water available for events?",
                a: "Yes. Ke Aloha Water can be provided for events, hospitality, and corporate occasions. Reach out to everything@8eight8.net for event pricing and availability.",
              },
              {
                q: "How do I contact Ke Aloha Water?",
                a: "You can reach us by email at everything@8eight8.net, through Instagram at @kealohawater, or by submitting an inquiry through the contact form on this website.",
              },
            ].map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} index={i} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Contact Section ─── */}
      <section id="contact" className="py-24 md:py-32 bg-[#0a0f1e]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">Connect</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-6" data-testid="contact-heading">
              Experience The Love
            </h2>
            <p className="text-base text-slate-400 font-light max-w-xl mx-auto leading-relaxed">
              Ready to discover the pure taste of KE ALOHA? Submit your information and we'll connect you with our sacred hydration.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="bg-slate-900/40 backdrop-blur-xl border border-white/10 p-8 md:p-12"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] text-slate-300 uppercase mb-3">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your full name"
                    className="w-full bg-slate-900/50 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-sky-400/50 focus:ring-1 focus:ring-sky-400/50 transition-all text-base placeholder:text-slate-600 font-light"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] text-slate-300 uppercase mb-3">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="w-full bg-slate-900/50 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-sky-400/50 focus:ring-1 focus:ring-sky-400/50 transition-all text-base placeholder:text-slate-600 font-light"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold tracking-[0.15em] text-slate-300 uppercase mb-3">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about your interest in KE ALOHA Water..."
                  className="w-full bg-slate-900/50 border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-sky-400/50 focus:ring-1 focus:ring-sky-400/50 transition-all text-base placeholder:text-slate-600 font-light resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <motion.button
                type="submit"
                disabled={formState === "sending"}
                whileHover={{ scale: formState === "sending" ? 1 : 1.02 }}
                whileTap={{ scale: formState === "sending" ? 1 : 0.98 }}
                className={`w-full py-4 font-medium text-sm tracking-wide transition-all duration-300 ${
                  formState === "sending"
                    ? "bg-slate-700 text-slate-400 cursor-wait"
                    : "bg-white text-slate-950 hover:bg-slate-200"
                }`}
                data-testid="contact-submit-button"
              >
                {formState === "sending" ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  "Submit Your Information"
                )}
              </motion.button>

              {/* Status messages */}
              <AnimatePresence>
                {formState === "success" && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-center text-sm text-emerald-400 font-light"
                    data-testid="contact-success-message"
                  >
                    Thank you! Your information has been received. We'll be in touch soon.
                  </motion.p>
                )}
                {formState === "error" && (
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="text-center text-sm text-red-400 font-light"
                    data-testid="contact-error-message"
                  >
                    Something went wrong. Please try again or contact us at everything@8eight8.net
                  </motion.p>
                )}
              </AnimatePresence>
            </form>
          </motion.div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-16 bg-[#020617] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12 md:gap-16">
            <div>
              <h3 className="font-heading text-2xl font-light text-white mb-4 tracking-wide">KE ALOHA WATER</h3>
              <p className="text-sm text-slate-500 font-light leading-relaxed mb-3">
                Sacred Hydration. Wellness in a bottle.
              </p>
              <p className="text-xs text-slate-600 font-light">
                In Hawaiian, KE ALOHA means "The Love"
              </p>
            </div>

            <div>
              <h4 className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">Products</h4>
              <ul className="space-y-3 text-sm text-slate-500 font-light">
                <li>500ml Bottles</li>
                <li>1 Liter Bottles</li>
                <li>Bulk Orders</li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-6">Connect With Us</h4>
              <div className="space-y-4">
                <a
                  href="mailto:everything@8eight8.net"
                  className="flex items-center gap-3 text-sm text-slate-500 font-light hover:text-white transition-colors duration-300"
                  data-testid="footer-email-link"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span>everything@8eight8.net</span>
                </a>
                <a
                  href="https://instagram.com/kealohawater"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-slate-500 font-light hover:text-white transition-colors duration-300"
                  data-testid="footer-instagram-link"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                  <span>@kealohawater</span>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 mt-12 pt-8 text-center">
            <p className="text-xs text-slate-600 font-light">&copy; 2025 KE ALOHA WATER. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
